import React from "react";
import { Container, Row, Col, Card, CardBody, Button, Label, Input, FormFeedback } from "reactstrap";
import { withTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";

import Breadcrumbs from "../../components/Common/Breadcrumb";
import Snackbars from "../../Utils/Snackbars";
import useRequests from '../../Utils/useRequests';
import { formatCurrency } from '../../Utils/formatters';

const colourStyles = {
  control: (styles, { isDisabled, isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isDisabled ? undefined : "#fff",
    ':active': {
      ...styles[':active'],
      backgroundColor: !isDisabled ? (isSelected ? "#fff" : "#036B41") : undefined,
    },
  }),
  menu: base => ({ ...base, zIndex: 100 }),
};

const UnfreezeEuroWallet = props => {
  document.title = 'Unfreeze Euro from User Wallet';
  const { getRequest, postRequest } = useRequests();

  const [AlertData, setAlertData] = React.useState({ variant: '', message: '' });
  const resetAlertData = () => {
    setTimeout(() => setAlertData({ variant: '', message: '' }), 5000);
  };

  // Currently frozen amount for the selected user
  const [FrozenBalance, setFrozenBalance] = React.useState(null);

  const validation1 = useFormik({
    enableReinitialize: true,
    initialValues: {
      user: null,
      amount: '',
    },
    validationSchema: Yup.object({
      user: Yup.object().nullable().required('Please select a user'),
      amount: Yup.string()
        .matches(/^[0-9.]+$/, 'Please enter a valid amount')
        .required('Amount is required'),
    }),
    onSubmit: async (values) => {
      try {
        let submit1 = document.getElementById('submit1');
        submit1.disabled = true;
        submit1.textContent = 'Processing...';

        const jsonpar = JSON.stringify({
          u_id: values.user.value,
          amount: parseFloat(values.amount),
          description: 'Unfreeze Euro',
        });

        const res = await postRequest('unfreeze-user-euro', jsonpar);
        if (res && res.code === 200) {
          setAlertData({ variant: 'success', message: res.message });
          validation1.resetForm();
          setFrozenBalance(null);
        } else {
          setAlertData({ variant: 'error', message: res.message });
        }
        resetAlertData();
        submit1.disabled = false;
        submit1.textContent = 'Unfreeze EUR';
      } catch (error) {
        console.error("Error : ", error);
      }
    }
  });

  // Load frozen balance when user changes
  const handleUserChange = async (selected) => {
    validation1.setFieldValue('user', selected);
    validation1.setFieldTouched('user', true);
    setFrozenBalance(null);

    if (!selected) return;

    console.log('Fetching wallet for user:', selected.value);
    const res = await postRequest('get-user-wallet', JSON.stringify({ u_id: selected.value }));
    console.log('Wallet API Response:', res);
    
    if (res && res.code === 200) {
      console.log('Frozen balance from API:', res.data?.euro_freezed);
      setFrozenBalance(parseFloat(res.data?.euro_freezed || 0));
    } else {
      console.error('Failed to fetch wallet:', res?.message);
      setAlertData({ variant: 'error', message: res?.message || 'Failed to fetch wallet data' });
      resetAlertData();
    }
  };

  const [UsersData, setUsersData] = React.useState([]);
  const handleLoadGetUsers = async () => {
    const res = await getRequest('get-users');
    if (res && res.code === 200) {
      let tempArr = res.data.map(element => ({
        label: `${element.first_name} ${element.last_name} (${element.email})`,
        value: element.u_id,
      }));
      setUsersData([{ options: tempArr }]);
    } else {
      setAlertData({ variant: 'error', message: res.message });
      resetAlertData();
    }
  };

  React.useEffect(() => {
    handleLoadGetUsers();
  }, []);

  return (
    <React.Fragment>
      {AlertData.message !== '' ? <Snackbars variant={AlertData.variant} message={AlertData.message} /> : ''}
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={props.t("Tokenizedgreen")} breadcrumbItem={props.t("Unfreeze Euro from User Wallet")} />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <Row>
                    {/* User dropdown */}
                    <Col md={6} xs="12">
                      <div className="mb-3 Register-Select2">
                        <Label className="form-label">Select User :</Label>
                        <Select
                          styles={colourStyles}
                          value={validation1.values.user}
                          onChange={handleUserChange}
                          onBlur={() => validation1.setFieldTouched('user', true)}
                          options={UsersData}
                          classNamePrefix="select2-selection"
                          placeholder="Search and select user..."
                          isClearable
                        />
                        {validation1.touched.user && validation1.errors.user ? (
                          <div style={{ display: 'block', color: '#f46a6a', fontSize: '12px', marginTop: '0.25rem' }}>
                            {validation1.errors.user}
                          </div>
                        ) : null}
                      </div>
                    </Col>

                    {/* Current Frozen Amount (Read-only) */}
                    <Col md={6} xs="12">
                      <div className="mb-3">
                        <Label className="form-label">Current Frozen Amount :</Label>
                        <Input
                          type="text"
                          value={FrozenBalance !== null ? formatCurrency(FrozenBalance) : ''}
                          placeholder="Select a user to view frozen amount"
                          readOnly
                          disabled
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                    </Col>

                    {/* Unfreeze amount */}
                    <Col md={6} xs="12">
                      <div className="mb-3">
                        <Label className="form-label">Unfreeze Amount :</Label>
                        <Input
                          name="amount"
                          type="text"
                          placeholder="Enter EUR amount to unfreeze"
                          onChange={validation1.handleChange}
                          onBlur={validation1.handleBlur}
                          value={validation1.values.amount || ""}
                          invalid={validation1.touched.amount && validation1.errors.amount ? true : false}
                        />
                        {validation1.touched.amount && validation1.errors.amount ? (
                          <FormFeedback type="invalid">{validation1.errors.amount}</FormFeedback>
                        ) : null}
                      </div>
                    </Col>
                  </Row>

                  <div className="text-left mt-2">
                    <Button
                      type="button"
                      color="primary"
                      id="submit1"
                      onClick={(e) => { e.preventDefault(); validation1.handleSubmit(); }}
                      className="me-2"
                    >
                      Unfreeze EUR
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(UnfreezeEuroWallet);
